/**
 * DigestPreferences — small settings card on the user Dashboard letting
 * them opt in/out of the weekly share-digest email.
 */
import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Mail } from "lucide-react";

export default function DigestPreferences() {
  const [enabled, setEnabled] = useState(null);
  const [lastSent, setLastSent] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    http.get("/share-stats/digest-preferences")
      .then((r) => { setEnabled(!!r.data.enabled); setLastSent(r.data.last_sent_at || null); })
      .catch(() => setEnabled(true));
  }, []);

  const toggle = async () => {
    if (enabled === null) return;
    const next = !enabled;
    setBusy(true);
    try {
      await http.post("/share-stats/digest-preferences", { enabled: next });
      setEnabled(next);
      toast.success(next ? "Weekly digest turned on" : "Weekly digest turned off");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save preference");
    } finally { setBusy(false); }
  };

  const openPreview = () => {
    window.open("/api/share-stats/digest-preview", "_blank", "noopener,noreferrer");
  };

  return (
    <div id="preferences" className="wofy-card mt-6" data-testid="digest-preferences">
      <div className="flex items-start gap-3">
        <div
          className="w-9 h-9 rounded-full flex items-center justify-center shrink-0"
          style={{ background: "var(--wofy-surface)" }}
        >
          <Mail className="w-4 h-4" style={{ color: "var(--wofy-primary)" }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="overline">WEEKLY SHARE DIGEST</div>
          <h3 className="font-semibold mt-1">Sunday recap of who's sharing your stories</h3>
          <p className="text-sm mt-1" style={{ color: "var(--wofy-text-soft)" }}>
            When your posts get 5+ shares in a week, we'll email you a summary with the per-network breakdown and your top posts.
          </p>
          {lastSent && (
            <p className="text-[11px] font-mono mt-2" style={{ color: "var(--wofy-text-soft)" }}>
              Last sent · {new Date(lastSent).toLocaleDateString(undefined, { dateStyle: "medium" })}
            </p>
          )}
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                data-testid="digest-toggle"
                checked={!!enabled}
                disabled={busy || enabled === null}
                onChange={toggle}
                className="h-4 w-4 rounded border"
                style={{ borderColor: "var(--wofy-border)", accentColor: "var(--wofy-primary)" }}
              />
              <span className="text-sm">Send me the weekly digest</span>
            </label>
            <button
              onClick={openPreview}
              data-testid="digest-preview-open"
              className="wofy-btn-ghost text-xs"
            >
              Preview email →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
